<template>
  <div
    class="queue-wait"
    role="timer"
    :aria-label="t('duel.searchingElapsed', { time: elapsedLabel })"
  >
    <span class="queue-dots" aria-hidden="true">
      <span></span><span></span><span></span>
    </span>
    <span class="queue-clock" aria-hidden="true">{{ elapsedLabel }}</span>
    <p class="queue-phase">{{ phaseLabel }}</p>
    <p v-if="nextLabel" class="queue-next">{{ nextLabel }}</p>
    <QueueRunner />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  QUEUE_WAIT_MID_MS,
  QUEUE_WAIT_OPEN_MS,
  QUEUE_WINDOW_MID,
  QUEUE_WINDOW_START,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import QueueRunner from './QueueRunner.vue'

const MID_SEC = Math.round(QUEUE_WAIT_MID_MS / 1000)
const OPEN_SEC = Math.round(QUEUE_WAIT_OPEN_MS / 1000)

const props = defineProps<{
  elapsedSec: number
  elapsedLabel: string
}>()

const { t } = useI18n()

const phase = computed(() => {
  if (props.elapsedSec >= OPEN_SEC) return 'open' as const
  if (props.elapsedSec >= MID_SEC) return 'mid' as const
  return 'near' as const
})

const phaseLabel = computed(() => {
  if (phase.value === 'open') return t('duel.queueWindowOpen')
  if (phase.value === 'mid') return t('duel.queueWindowMid', { n: QUEUE_WINDOW_MID })
  return t('duel.queueWindowNear', { n: QUEUE_WINDOW_START })
})

const nextLabel = computed(() => {
  if (phase.value === 'open') return ''
  const left = phase.value === 'mid' ? OPEN_SEC - props.elapsedSec : MID_SEC - props.elapsedSec
  const key = phase.value === 'mid' ? 'duel.queueOpenIn' : 'duel.queueWidenIn'
  return t(key, { n: Math.max(0, left) })
})

</script>

<style scoped>
.queue-wait {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.queue-dots {
  display: flex;
  gap: 8px;
}

.queue-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e8c547;
  animation: queue-dot 1.2s var(--md-motion-standard, ease) infinite;
}

.queue-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.queue-dots span:nth-child(3) {
  animation-delay: 0.3s;
}

.queue-clock {
  font-size: 40px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: #fde68a;
  line-height: 1;
}

.queue-phase,
.queue-next,
.queue-score {
  margin: 0;
  max-width: 320px;
  text-align: center;
  font-size: 14px;
  line-height: 1.35;
  color: #cbd5e1;
}

.queue-phase {
  color: #fde68a;
}

.star-stage {
  position: relative;
  width: 220px;
  height: 108px;
  margin-top: 4px;
}

.star-big,
.star-small {
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fde68a;
  cursor: pointer;
}

.star-big {
  position: absolute;
  left: 50%;
  top: 46%;
  width: 72px;
  height: 72px;
  transform: translate(-50%, -50%);
}

.star-big:active {
  transform: translate(-50%, -50%) scale(0.92);
}

.star-small {
  position: absolute;
  width: 44px;
  height: 44px;
  color: #fff4c4;
  filter: drop-shadow(0 0 6px rgba(253, 230, 138, 0.85));
}

.star-pop {
  position: absolute;
  left: 50%;
  top: 0;
  color: #fde68a;
  font-size: 14px;
  font-weight: 700;
  pointer-events: none;
  animation: star-float 700ms var(--md-motion-standard, ease) forwards;
}

.coin-flash {
  position: absolute;
  left: 50%;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  transform: translateX(-50%);
  color: #fde68a;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

@keyframes queue-dot {
  0%,
  80%,
  100% {
    opacity: 0.25;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

@keyframes star-float {
  from {
    opacity: 1;
    transform: translate(-50%, 8px);
  }
  to {
    opacity: 0;
    transform: translate(-50%, -18px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .queue-dots span,
  .star-pop {
    animation: none;
  }

  .queue-dots span {
    opacity: 0.85;
  }

  .star-big:active {
    transform: translate(-50%, -50%);
  }
}
</style>
